import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ThemeContext } from 'styled-components';
import type { DropMenuItem } from 'venn-ui-kit';
import { Icon, Notifications, NotificationType, SimpleMenuItem } from 'venn-ui-kit';
import { compact, isNil } from 'lodash';
import { flattenNode, useHasFF, useModal } from 'venn-utils';
import type { Portfolio } from 'venn-api';
import { ActionsButton, GreyIcon } from './actions/common';
import EditCapitalModal from './edit-capital/EditCapitalModal';
import CreatePortfolioModal from '../modals/create-portfolio/CreatePortfolioModal';
import { UserContext } from '../contexts';
import ImportStrategyModal from './strategy/ImportStrategyModal';
import { AllocationPanelContext } from './AllocationPanelContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type MenuActionOption = DropMenuItem<(() => void) | undefined>;

interface AllocationStrategyActionsProps {
  isRoot: boolean;
  onAddStrategy: (strategy?: Portfolio) => void;
  onAddInvestment: () => void;
  onEditName: () => void;
  onDelete: () => void;
  onCompare?: () => void;
  compareLink?: string;
  strategy: Portfolio;
  onUpdateChildrenAllocations: (updatedStrategy: Portfolio) => void;
  isPercentageMode: boolean;
  rootName: string;
}

const AllocationStrategyActions = ({
  isRoot,
  onAddStrategy,
  onAddInvestment,
  onEditName,
  onDelete,
  onCompare,
  compareLink,
  strategy,
  onUpdateChildrenAllocations,
  isPercentageMode,
  rootName,
}: AllocationStrategyActionsProps) => {
  const hasImportExportStrategiesFF = useHasFF('import_export_strategies_ff');
  const hasRCMStrategyEnforcement = useHasFF('rcm_strategy_enforcement_ff');
  const hasRCMPortfolioAllocatorStrategyEnforcement = useHasFF('rcm_portfolio_strategy_enforcement_ff');

  const { Colors } = useContext(ThemeContext);
  const [isEditCapitalModalOpen, openEditCapitalModal, closeEditCapitalModal] = useModal();
  const hasFundsInSubtree = useMemo(
    () => flattenNode(strategy).filter((child) => !isNil(child.fund)).length > 0,
    [strategy],
  );

  const { hasPermission } = useContext(UserContext);
  const [isExportStrategyModalOpen, openExportStrategyModal, closeExportStrategyModal] = useModal();
  const onStrategySavedAsPortfolio = useCallback(
    (newPortfolio: Portfolio) => {
      closeExportStrategyModal();
      Notifications.notify(
        `Successfully saved '${strategy?.name}' strategy as a new portfolio named '${newPortfolio.name}'.`,
        NotificationType.SUCCESS,
      );
    },
    [closeExportStrategyModal, strategy?.name],
  );

  const [isImportStrategyModalOpen, openImportStrategyModal, closeImportStrategyModal] = useModal();
  const allocationPanelContext = useContext(AllocationPanelContext);
  const isLeverageShortPortfolio =
    allocationPanelContext.allocationPanelType === 'full' && allocationPanelContext.isLeverageShortPortfolio;
  const [open, setOpen] = useState(false);

  const sections: MenuActionOption[][] = [
    compact([
      !hasRCMPortfolioAllocatorStrategyEnforcement && {
        label: isRoot ? 'Add a Strategy' : 'Add a Sub-strategy',
        value: onAddStrategy,
      },
    ]),
    compact([
      (!hasRCMPortfolioAllocatorStrategyEnforcement || isRoot) && {
        label: 'Add an investment',
        value: onAddInvestment,
      },
    ]),
    compact([
      !isRoot &&
        !hasRCMStrategyEnforcement && {
          label: 'Edit Name',
          value: onEditName,
        },
      !isRoot &&
        !hasRCMPortfolioAllocatorStrategyEnforcement && {
          label: 'Delete',
          value: onDelete,
        },
      {
        label: 'Edit Capital',
        value: openEditCapitalModal,
        disabled: !hasFundsInSubtree || isPercentageMode || isLeverageShortPortfolio,
        tooltipContent: isLeverageShortPortfolio
          ? 'Not supported for Portfolios with Leverage or short positions.'
          : undefined,
      },
    ]),
    compact([
      hasImportExportStrategiesFF &&
        !isRoot &&
        hasPermission('CREATE_PORTFOLIO') && {
          label: 'Save as a portfolio',
          value: openExportStrategyModal,
          disabled: isPercentageMode,
        },
      hasImportExportStrategiesFF && {
        label: `Import portfolio as a ${isRoot ? 'strategy' : 'sub-strategy'}`,
        value: openImportStrategyModal,
      },
    ]),
    [
      {
        icon: (
          <Icon
            type="line-columns"
            prefix="fad"
            style={{ color: isNil(onCompare) ? Colors.GreyScale.Grey40 : Colors.Primary.Dark, marginRight: 8 }}
          />
        ),
        label: 'Compare all investments',
        value: onCompare,
        disabled: isNil(onCompare),
        link: isNil(onCompare) ? undefined : compareLink,
      },
    ],
  ].filter((section: MenuActionOption[]) => section.length > 0);

  const dropdownMenu = (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild data-testid="qa-strategy-actions" className="qa-strategy-actions">
        <ActionsButton
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
        >
          <GreyIcon iconLabel="strategy actions icon" type="ellipsis-h" />
        </ActionsButton>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
          {sections.map((options, index) => (
            <>
              {options.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  className="!px-0 !py-0"
                  onPointerLeave={(e) => e.preventDefault()}
                  onPointerMove={(e) => e.preventDefault()}
                >
                  <SimpleMenuItem
                    item={item}
                    highlighted={false}
                    selected={false}
                    onClick={() => {
                      !item.disabled && item?.value?.();
                      setOpen(false);
                    }}
                  />
                </DropdownMenuItem>
              ))}
              {index < sections.length - 1 && <DropdownMenuSeparator />}
            </>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
  return (
    <>
      {dropdownMenu}
      {isEditCapitalModalOpen && (
        <EditCapitalModal
          isRoot={isRoot}
          strategy={strategy}
          onClose={closeEditCapitalModal}
          onSubmit={onUpdateChildrenAllocations}
        />
      )}
      {isExportStrategyModalOpen && (
        <CreatePortfolioModal
          isCreatingFromStrategy
          defaultName={`${rootName}: ${strategy.name}`}
          onClose={closeExportStrategyModal}
          onSubmit={onStrategySavedAsPortfolio}
          baseline={strategy}
          disableBaselineSelection
          hideBasePortfolio
          canCreateFromScratch={false}
          createDraft={false}
        />
      )}
      {isImportStrategyModalOpen && (
        <ImportStrategyModal isAddingToRoot={isRoot} onSubmit={onAddStrategy} onClose={closeImportStrategyModal} />
      )}
    </>
  );
};

export default AllocationStrategyActions;
